<template>
  <PageHeader
    title="Roles & Permissions"
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: 'Users' },
      { label: 'Roles & Permissions' }
    ]"
  >
    <template #right>
      <Button
        :label="$t('ui.crud.new', { item: $t('ui.common.role') })"
        severity="primary"
        icon="plus"
        @click="addNewRole()"
      />
    </template>
  </PageHeader>
  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="RolesPermissions"
      :value="rolesPermissionsData"
      removable-sort
      data-key="id"
      sort-field="roles"
      :sort-order="-1"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :global-filter-fields="['name']"
      :rows="20"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-settings-roles-permissions"
      state-storage="session"
      :loading="isLoading"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
    >
      <template #empty>
        <Empty
          v-if="!isLoading"
          :title="callCenterSelected ? 'No data' : `Select a ${$t('ui.entities.departments.callCenter')}`"
        />
      </template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
        <div class="flex justify-between mt-4">
          <span>
            <Button
              label="CSV"
              type="button"
              severity="secondary"
              outlined
              icon="file-excel"
              @click="exportCSV($event)"
            />
          </span>
        </div>
      </template>

      <Column
        field="name"
        header="Role"
      >
        <template #body="{ data }">
          <Chip :color="data.color">{{ data.name }}</Chip>
        </template>
      </Column>

      <Column
        field="permissions"
        header="Permissions"
      >
        <template #body="{ data }">
          {{ data.permissions.length }}
        </template>
      </Column>

      <Column>
        <template #body="{ data }">
          <Button
            v-tippy="$t('ui.actions.edit')"
            icon="pencil"
            plain
            severity="secondary"
            @click="editRole(data.id)"
          />
        </template>
      </Column>
    </DataTable>
  </Card>

  <teleport to="#modals-container">
    <Modal
      :is-open="roleModalIsOpen"
      :title="isNewRole ? 'New Role' : 'Edit Role'"
      :has-unsaved-changes="editRoleHasUnsavedChanges"
      min-width="400"
      icon="user-plus"
      :buttons="[]"
      :disable-close="editRoleHasUnsavedChanges"
      :is-confirming="isNewRole ? addRoleIsLoading : updateRoleIsLoading"
      @close-modal="closeRoleModal()"
      @ok-modal="isNewRole ? saveRole() : updateRole()"
    >
      <FormGroup
        label="Name"
        :is-required="v$.name.required?.$invalid"
      >
        <Input v-model="roleState.name" />
        <InputError :has-error="v$.name.$error">{{ v$.name.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <FormGroup
        label="User Type"
        :is-required="v$.userType.required?.$invalid"
      >
        <Select
          v-model="roleState.userType"
          :options="[
            { value: 1, label: 'Internal' },
            { value: 2, label: 'External' }
          ]"
          :chips="false"
          value-key="value"
          label-key="label"
          placeholder="Select"
          class="w-full"
        >
          <template #option="slotProps">
            {{ slotProps.option.label }}
          </template>
        </Select>
        <InputError :has-error="v$.userType.$error">{{ v$.userType.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <FormGroup
        label="Permissions"
        :is-required="v$.permissions.required?.$invalid"
      >
        <Select
          v-model="roleState.permissions"
          :options="permissions"
          mode="multiple"
          :chips="false"
          searchable
          value-key="id"
          label-key="name"
          placeholder="Select"
          class="w-full"
        >
          <template #option="slotProps">
            {{ slotProps.option.name }}
          </template>
        </Select>
        <InputError :has-error="v$.permissions.$error">{{ v$.permissions.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <FormGroup
        label="Colour"
        :is-required="v$.color.required?.$invalid"
      >
        <Input v-model="roleState.color" />
        <InputError :has-error="v$.color.$error">{{ v$.color.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <FormGroup
        label="Level"
        :is-required="v$.level.required?.$invalid"
      >
        <Select
          v-model="roleState.level"
          :options="levelOptions"
          :chips="false"
          value-key="value"
          label-key="label"
          placeholder="Select"
          class="w-full"
        >
          <template #option="slotProps">
            {{ slotProps.option.label }}
          </template>
        </Select>
        <InputError :has-error="v$.level.$error">{{ v$.level.$errors[0]?.$message }}</InputError>
      </FormGroup>

      <div class="flex justify-end mt-8">
        <div class="w-full">
          <Button
            v-if="!isNewRole"
            severity="danger"
            label="Delete"
            plain
            :is-loading="deleteRoleIsLoading"
            @click="deleteRole()"
          />
        </div>
        <div class="flex justify-end gap-2">
          <Button
            :label="$t('ui.actions.cancel')"
            severity="secondary"
            outlined
            type="button"
            :disabled="addRoleIsLoading"
            @click="closeRoleModal()"
          />
          <Button
            :label="$t('ui.actions.save')"
            :is-loading="addRoleIsLoading"
            @click="isNewRole ? saveRole() : updateRole()"
          />
        </div>
      </div>
    </Modal>
  </teleport>
</template>

<script setup>
import { ref, watch } from 'vue'

import Card from '@/components/card/Card.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import Button from '@/components/button/Button.vue'
import Select from '@/components/forms/Select.vue'
import Empty from '@/components/empty/Empty.vue'
import Modal from '@/components/modal/Modal.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Swal from 'sweetalert2'
import { FilterMatchMode } from 'primevue/api'

import useApiRequest from '@/composables/useApiRequest'
import { alertToast } from '@/utilities/notification'
import Chip from '@/components/chip/Chip.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

const dt = ref()
const rolesPermissionsRequest = useApiRequest()
const isLoading = rolesPermissionsRequest.isLoading
const filters = ref([])
const rolesPermissionsData = ref(null)
const permissions = ref(null)

const editRoleHasUnsavedChanges = ref(false)
const roleModalIsOpen = ref(false)
const roleState = ref(null)
const roleStateInit = ref(null)
const searchTerm = ref(null)

const isNewRole = ref(false)
const roleHasUnsavedChanges = ref(false)
const roleStateDefault = {
  name: null,
  userType: null,
  permissions: [],
  color: null,
  level: null
}

const levelOptions = [
  {
    value: 'region',
    label: 'Global'
  },
  {
    value: 'manufacturer',
    label: 'Manufacturer'
  },
  {
    value: 'group',
    label: 'Group'
  },
  {
    value: 'dealership',
    label: 'Dealership'
  }
]

const newRoleRules = {
  name: { required },
  userType: { required },
  permissions: { required },
  color: {},
  level: {}
}

const v$ = useVuelidate(newRoleRules, roleState)

function closeRoleModal() {
  roleState.value = { ...roleStateInit }
  roleModalIsOpen.value = false
  roleHasUnsavedChanges.value = false
}

function fetchRolesPermissions() {
  rolesPermissionsRequest.send({ method: 'GET', endpoint: '/v1/roles' }).then(response => {
    if (response.success) {
      rolesPermissionsData.value = response.data.roles
      permissions.value = response.data.permissions
    }
  })
}
fetchRolesPermissions()

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS }
  }
}
initFilters()

function clearFilters() {
  initFilters()
}

function editRole(id) {
  isNewRole.value = false
  roleModalIsOpen.value = true
  roleState.value = JSON.parse(JSON.stringify(rolesPermissionsData.value.find(role => role.id == id)))
  roleStateInit.value = JSON.parse(JSON.stringify(roleState.value))
}

function addNewRole() {
  isNewRole.value = true
  roleModalIsOpen.value = true
  roleState.value = JSON.parse(JSON.stringify(roleStateDefault))
  roleStateInit.value = JSON.parse(JSON.stringify(roleState.value))
}

const deleteRoleApiRequest = useApiRequest()
const deleteRoleIsLoading = deleteRoleApiRequest.isLoading.value

function deleteRole() {
  Swal.fire({
    title: 'Are you sure?',
    icon: 'warning',
    reverseButtons: true,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes, delete this role',
    confirmButtonColor: 'var(--bg-error-solid)',
    cancelButtonText: 'Cancel'
  }).then(function (response) {
    if (response.isConfirmed) {
      deleteRoleApiRequest.send({ method: 'DELETE', endpoint: `v1/roles/${roleState.value.id}` }).then(response => {
        if (response.success) {
          alertToast('Role deleted', null, 'success')
          const role = rolesPermissionsData.value.find(role => role.id == roleState.value.id)
          const index = rolesPermissionsData.value.indexOf(role)
          if (index != -1) {
            rolesPermissionsData.value.splice(index, 1)
          }
          closeEditRoleModal()
        } else {
          alertToast('Failed to delete role', response.message, 'error')
        }
      })
    }
  })
}

const addRoleApiRequest = useApiRequest()
const addRoleIsLoading = addRoleApiRequest.isLoading.value

async function saveRole() {
  const isFormCorrect = await v$.value.$validate()
  if (!isFormCorrect) {
    return
  }

  try {
    addRoleApiRequest
      .send({ method: 'POST', endpoint: '/v1/roles', data: JSON.stringify(roleState.value) })
      .then(response => {
        if (response.success) {
          alertToast('Role added', null, 'success')
          fetchRolesPermissions()
          closeRoleModal()
        } else {
          alertToast('Failed to add role', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to add role', err.message, 'error')
  }
}

const updateRoleApiRequest = useApiRequest()
const updateRoleIsLoading = updateRoleApiRequest.isLoading.value

async function updateRole() {
  try {
    updateRoleApiRequest
      .send({
        method: 'PATCH',
        endpoint: `v1/roles/${roleState.value.id}`,
        data: JSON.stringify(roleState.value)
      })
      .then(response => {
        if (response.success) {
          alertToast('Role updated', null, 'success')
          const role = rolesPermissionsData.value.find(role => role.id == roleState.value.id)
          const index = rolesPermissionsData.value.indexOf(role)
          if (index != -1) {
            rolesPermissionsData.value[index] = roleState.value
          }
          closeRoleModal()
        } else {
          alertToast('Failed to update role', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to update role', err.message, 'error')
  }
}

function closeEditRoleModal() {
  roleModalIsOpen.value = false
  editRoleHasUnsavedChanges.value = false
  roleState.value = null
  roleStateInit.value = null
  searchTerm.value = null
}

const exportCSV = () => {
  dt.value.exportCSV()
}

watch(
  () => roleState.value,
  () => {
    if (!roleState.value) {
      return
    }

    const init = JSON.stringify(roleStateInit.value)
    const update = JSON.stringify(roleState.value)

    if (init == update) {
      editRoleHasUnsavedChanges.value = false
    } else {
      editRoleHasUnsavedChanges.value = true
    }
  },
  { deep: true }
)
</script>

<style></style>
